/* eslint-disable max-len */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'app/AppStore';
import { getLastVehicleDetail } from 'domains/catalog/Catalog.store';
import { getPlannedOperations } from 'domains/maintenancePlan/MaintenancePlan.store';
import { getDealerType } from 'domains/user';
import { MaintenanceViewProps } from 'pages/CatalogPage/DH/Maintenance/Maintenance';
import { MileageIntervalTable, OperationTable } from 'pages/CatalogPage/DH/Maintenance/Maintenance.styled';
import {
  getMileageOperationColumns,
  getMileageSectionColumns,
  getUnitsName,
  OperationColumnType,
} from 'pages/CatalogPage/DH/Maintenance/Maintenance.utils';
import { Flex, Text } from 'UI';
import { getData } from 'utils';

function getOperationYears(fromYearsTest: string, everyYears: string): string[] {
  return Array.from({ length: 12 }, (_, i) => i + 1).reduce((acc, current) => {
    const fromYears = Number(fromYearsTest) || 0;
    if (fromYears > current ? false : (current - fromYears) % Number(everyYears) === 0) {
      acc.push(`year_${current}`);
    }
    return acc;
  }, [] as string[]);
}

const MileageTimeSection = ({ maintenancePlanData, getLabel, units, printView, val }: MaintenanceViewProps) => {
  const { t } = useTranslation();
  const vehicleDetail = useSelector(getLastVehicleDetail);
  const operationsResponse = useSelector((state: RootState) => getPlannedOperations(state, vehicleDetail?.vin));
  const unitsKm = units ?? 'km';
  const dealerType = useSelector(getDealerType);
  const operationsResponseData = getData(operationsResponse);
  const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);

  if (maintenancePlanData.operationByMileageOrTimeList.length === 0) {
    return <></>;
  }

  const columns = getMileageSectionColumns(getLabel, t, unitsKm, hoveredColumn, setHoveredColumn);
  const data = maintenancePlanData.operationByMileageOrTimeList.map((plan) => {
    const operationData = operationsResponseData?.data?.operations?.find(
      (o) => o.code === plan.codeAction + plan.codeLibele,
    );
    return {
      ...plan,
      status1: plan.codeAction + plan.codeLibele,
      status: operationData?.isOpen ?? 3,
      nextMileage: operationData?.nextMileage ?? 0,
      nextMileageUnit: operationData?.mileageUnit ?? 'km',
      operationYears: getOperationYears(plan.fromYears, plan.everyYears),
    };
  });
  const upcomingOperations = data.filter((record) => record.status === 0);
  const currentOperations = data.filter((record) => record.status === 1);
  const delayedOperations = data.filter((record) => record.status === 2);

  return (
    <>
      {printView && (
        <>
          <Text type={'h5_bold'}>{getLabel(27)}</Text>
          {(dealerType === 'R1' || dealerType === 'R2') && (
            <Flex direction={'row'}>
              <Text type="base" bold>{`${t('catalog.vehicle.mileage', 'Mileage')}:`}</Text>
              <Text type="base" transform={'lowercase'}>
                &nbsp;{`${val} ${getUnitsName(t, unitsKm)}`}
              </Text>
            </Flex>
          )}
        </>
      )}
      <MileageIntervalTable<OperationColumnType>
        rowKey={'id'}
        columns={columns}
        dataSource={data}
        pagination={false}
        bordered
      />
      {(dealerType === 'R1' || dealerType === 'R2') && (
        <Flex direction={'row'} gap={printView ? 15 : 33} wrap={'wrap'}>
          {delayedOperations.length > 0 && (
            <Flex basis={printView ? '100%' : '30%'} minWidth={400}>
              <OperationTable<OperationColumnType>
                rowKey={'id'}
                columns={getMileageOperationColumns(t, unitsKm, 2)}
                bordered
                dataSource={delayedOperations}
                pagination={false}
              />
            </Flex>
          )}
          {currentOperations.length > 0 && (
            <Flex direction={'column'} gap={printView ? 15 : 33} basis={printView ? '100%' : '30%'} minWidth={400}>
              <OperationTable<OperationColumnType>
                rowKey={'id'}
                columns={getMileageOperationColumns(t, unitsKm, 1)}
                bordered
                dataSource={currentOperations}
                pagination={false}
              />
            </Flex>
          )}
          {upcomingOperations.length > 0 && (
            <Flex direction={'column'} gap={printView ? 15 : 33} basis={printView ? '100%' : '30%'} minWidth={400}>
              <OperationTable<OperationColumnType>
                rowKey={'id'}
                columns={getMileageOperationColumns(t, unitsKm, 0)}
                bordered
                dataSource={upcomingOperations}
                pagination={false}
              />
            </Flex>
          )}
        </Flex>
      )}
    </>
  );
};
export default MileageTimeSection;
